import { brickSeed } from './hash';
import { DEFAULT_WALL_SPEC, type WallSpec } from './types';

export interface ResolvedWallSpec extends WallSpec {
  pitchX: number;
  pitchY: number;
  nCols: number;
  nRows: number;
  originX: number;
  originY: number;
  actualWidth: number;
  actualHeight: number;
}

export interface BrickPlacement {
  index: number;
  col: number;
  course: number;
  x: number;
  y: number;
  z: number;
  seed: number;
  left: number;
  right: number;
  bottom: number;
  top: number;
}

export type JointKind = 'head' | 'bed';

export interface JointDef {
  id: number;
  a: number;
  b: number;
  kind: JointKind;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface WallGrid {
  spec: ResolvedWallSpec;
  bricks: BrickPlacement[];
  joints: JointDef[];
  brickJoints: number[][];
  jointAdj: number[][];
  neighbors: number[][];
}

function finiteOr(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

export function resolveWallSpec(partial?: Partial<WallSpec>): ResolvedWallSpec {
  const width = Math.max(0.6, finiteOr(partial?.width ?? DEFAULT_WALL_SPEC.width, DEFAULT_WALL_SPEC.width));
  const height = Math.max(0.4, finiteOr(partial?.height ?? DEFAULT_WALL_SPEC.height, DEFAULT_WALL_SPEC.height));
  const brickLength = Math.max(0.05, finiteOr(partial?.brickLength ?? DEFAULT_WALL_SPEC.brickLength, DEFAULT_WALL_SPEC.brickLength));
  const brickHeight = Math.max(0.03, finiteOr(partial?.brickHeight ?? DEFAULT_WALL_SPEC.brickHeight, DEFAULT_WALL_SPEC.brickHeight));
  const brickDepth = Math.max(0.03, finiteOr(partial?.brickDepth ?? DEFAULT_WALL_SPEC.brickDepth, DEFAULT_WALL_SPEC.brickDepth));
  const joint = Math.max(0.002, finiteOr(partial?.joint ?? DEFAULT_WALL_SPEC.joint, DEFAULT_WALL_SPEC.joint));
  const seed = (finiteOr(partial?.seed ?? DEFAULT_WALL_SPEC.seed, DEFAULT_WALL_SPEC.seed) | 0) >>> 0;
  const pitchX = brickLength + joint;
  const pitchY = brickHeight + joint;
  const nCols = Math.max(2, Math.round(width / pitchX));
  const nRows = Math.max(2, Math.round(height / pitchY));
  const actualWidth = nCols * pitchX - joint;
  const actualHeight = nRows * pitchY - joint;
  const originX = -actualWidth / 2;
  const originY = 0;
  return {
    width,
    height,
    brickLength,
    brickHeight,
    brickDepth,
    joint,
    seed,
    pitchX,
    pitchY,
    nCols,
    nRows,
    originX,
    originY,
    actualWidth,
    actualHeight,
  };
}

const SNAP = 0.005;

export function snapLattice(v: number): number {
  return Math.round(v / SNAP) * SNAP;
}

export function buildWallGrid(partial?: Partial<WallSpec>): WallGrid {
  const spec = resolveWallSpec(partial);
  const { nCols, nRows, pitchX, pitchY, originX, originY, brickLength, brickHeight, seed } = spec;
  const bricks: BrickPlacement[] = [];

  for (let course = 0; course < nRows; course += 1) {
    const odd = course % 2 === 1;
    const xOff = odd ? pitchX * 0.5 : 0;
    const y = originY + course * pitchY + brickHeight * 0.5;
    for (let col = 0; col < nCols; col += 1) {
      const x = originX + col * pitchX + brickLength * 0.5 + xOff;
      const index = bricks.length;
      bricks.push({
        index,
        col,
        course,
        x,
        y,
        z: 0,
        seed: brickSeed(col, course, seed),
        left: x - brickLength * 0.5,
        right: x + brickLength * 0.5,
        bottom: y - brickHeight * 0.5,
        top: y + brickHeight * 0.5,
      });
    }
  }

  const joints: JointDef[] = [];
  const byCourse: number[][] = Array.from({ length: nRows }, () => []);
  for (const b of bricks) byCourse[b.course].push(b.index);
  for (const row of byCourse) {
    row.sort((i, j) => bricks[i].x - bricks[j].x);
  }

  const overlapMin = brickLength * 0.22;

  for (const row of byCourse) {
    for (let i = 0; i < row.length - 1; i += 1) {
      const a = bricks[row[i]];
      const b = bricks[row[i + 1]];
      const gap = b.left - a.right;
      if (gap < 0 || gap > spec.joint * 2.2) continue;
      const x = snapLattice((a.right + b.left) * 0.5);
      const y0 = snapLattice(Math.max(a.bottom, b.bottom));
      const y1 = snapLattice(Math.min(a.top, b.top));
      if (y1 - y0 < brickHeight * 0.4) continue;
      joints.push({
        id: joints.length,
        a: a.index,
        b: b.index,
        kind: 'head',
        x0: x,
        y0,
        x1: x,
        y1,
      });
    }
  }

  for (let c = 0; c < nRows - 1; c += 1) {
    for (const ia of byCourse[c]) {
      const a = bricks[ia];
      for (const ib of byCourse[c + 1]) {
        const b = bricks[ib];
        const x0 = Math.max(a.left, b.left);
        const x1 = Math.min(a.right, b.right);
        if (x1 - x0 < overlapMin) continue;
        const y = snapLattice((a.top + b.bottom) * 0.5);
        joints.push({
          id: joints.length,
          a: a.index,
          b: b.index,
          kind: 'bed',
          x0: snapLattice(x0),
          y0: y,
          x1: snapLattice(x1),
          y1: y,
        });
      }
    }
  }

  const brickJoints: number[][] = Array.from({ length: bricks.length }, () => []);
  const neighbors: number[][] = Array.from({ length: bricks.length }, () => []);
  for (const j of joints) {
    brickJoints[j.a].push(j.id);
    brickJoints[j.b].push(j.id);
    if (!neighbors[j.a].includes(j.b)) neighbors[j.a].push(j.b);
    if (!neighbors[j.b].includes(j.a)) neighbors[j.b].push(j.a);
  }

  type EndKey = string;
  const atEnd = new Map<EndKey, number[]>();
  const addEnd = (x: number, y: number, id: number) => {
    const key = `${snapLattice(x).toFixed(3)},${snapLattice(y).toFixed(3)}`;
    let list = atEnd.get(key);
    if (!list) {
      list = [];
      atEnd.set(key, list);
    }
    if (!list.includes(id)) list.push(id);
  };
  for (const j of joints) {
    addEnd(j.x0, j.y0, j.id);
    addEnd(j.x1, j.y1, j.id);
  }

  const jointAdj: number[][] = Array.from({ length: joints.length }, () => []);
  const link = (a: number, b: number) => {
    if (a === b) return;
    if (!jointAdj[a].includes(b)) jointAdj[a].push(b);
    if (!jointAdj[b].includes(a)) jointAdj[b].push(a);
  };
  for (const ids of atEnd.values()) {
    for (let i = 0; i < ids.length; i += 1) {
      for (let k = i + 1; k < ids.length; k += 1) link(ids[i], ids[k]);
    }
  }
  // Head and bed joints meet at brick corners but their geometric endpoints
  // sit in the middle of the mortar gap — link them through the shared brick
  // so pathfinding can walk the staircase lattice.
  for (const list of brickJoints) {
    for (let i = 0; i < list.length; i += 1) {
      for (let k = i + 1; k < list.length; k += 1) link(list[i], list[k]);
    }
  }

  return { spec, bricks, joints, brickJoints, jointAdj, neighbors };
}

export function jointMid(j: JointDef): { x: number; y: number } {
  return { x: (j.x0 + j.x1) * 0.5, y: (j.y0 + j.y1) * 0.5 };
}

export function jointsShareVertex(a: JointDef, b: JointDef): boolean {
  const pts = [
    [a.x0, a.y0],
    [a.x1, a.y1],
  ];
  const qts = [
    [b.x0, b.y0],
    [b.x1, b.y1],
  ];
  for (const p of pts) {
    for (const q of qts) {
      if (Math.abs(p[0] - q[0]) < SNAP * 0.6 && Math.abs(p[1] - q[1]) < SNAP * 0.6) return true;
    }
  }
  return false;
}

export function closestBrickIndex(grid: WallGrid, x: number, y: number): number {
  let best = 0;
  let bestD = Infinity;
  for (const b of grid.bricks) {
    const d = (b.x - x) * (b.x - x) + (b.y - y) * (b.y - y);
    if (d < bestD) {
      bestD = d;
      best = b.index;
    }
  }
  return best;
}
